import { useState, useCallback, useRef, RefObject, useEffect } from 'react'

import { useTheme } from '@emotion/react'

import { useSpring, SpringValue } from 'react-spring'
// import { useResizeObserver } from '@/lib/hooks/resizeObserver'

interface Hooks {
  isShown: boolean
  handleClick: () => void
  containerSpring: Record<string, SpringValue>
  arrowSpring: Record<string, SpringValue>
  childSpring: Record<string, SpringValue>
  titleSpring: Record<string, SpringValue>
  titleRef: RefObject<HTMLHeadingElement>
  childRef: RefObject<HTMLDivElement>
}

export function useHooks(): Hooks {
  const titleRef = useRef<HTMLHeadingElement>(null)
  const childRef = useRef<HTMLDivElement>(null)

  const theme = useTheme()

  const [containerHeight, setContainerHeight] = useState(0)
  const [isShown, setDisplay] = useState(false)
  const [immediate, setImmediate] = useState(true)

  useEffect(() => {
    if (titleRef.current) {
      setContainerHeight(titleRef.current.getBoundingClientRect().height)
    }
  }, [titleRef.current])

  const handleResize = useCallback(() => {
    if (titleRef.current && childRef.current) {
      if (isShown) {
        setContainerHeight(
          childRef.current.getBoundingClientRect().height +
            titleRef.current.getBoundingClientRect().height,
        )
      } else {
        setContainerHeight(titleRef.current.getBoundingClientRect().height)
      }
    }
  }, [titleRef.current, childRef.current, isShown])

  useEffect(() => {
    handleResize()
  }, [isShown])

  useEffect(() => {
    window.addEventListener('resize', handleResize)

    return () => window.removeEventListener('resize', handleResize)
  }, [handleResize])

  // useResizeObserver(titleRef, handleResize)

  const handleClick = useCallback(() => {
    setDisplay(!isShown)
    setImmediate(false)
  }, [isShown, handleResize])

  const springConfig = {
    mass: 1,
    tension: 200,
    friction: 30,
  }

  const containerSpring = useSpring({
    from: {
      color: theme.colors.secondary[900],
      backgroundColor: theme.colors.gray[0],
    },
    to: {
      height: `${containerHeight}px`,
      color: isShown ? theme.colors.gray[0] : theme.colors.secondary[900],
      backgroundColor: isShown
        ? theme.colors.secondary[900]
        : theme.colors.gray[0],
    },
    immediate: immediate,
    config: springConfig,
  })

  const arrowSpring = useSpring({
    from: { transform: 'rotate(0deg)' },
    to: {
      transform: isShown ? 'rotate(90deg)' : 'rotate(0deg)',
    },
    config: springConfig,
  })

  const childSpring = useSpring({
    from: { opacity: 0 },
    to: {
      opacity: isShown ? 1 : 0,
    },
    config: springConfig,
  })

  const titleSpring = useSpring({
    from: { borderRadius: '16px 16px 0 22px' },
    to: {
      borderRadius: isShown ? '16px 16px 0 0px' : '16px 16px 0 22px',
    },
    config: springConfig,
  })

  return {
    isShown,
    titleRef,
    childRef,
    handleClick,
    containerSpring,
    arrowSpring,
    childSpring,
    titleSpring,
  }
}
